import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/design-system/lib/cn';
import { Badge } from '@/design-system/ui/Badge';
import { formatNumericDate } from '@escala/domain/dates';
import {
  COOKIE_CATEGORIES,
  COOKIE_KIND_LABEL,
  type CookieCategoryInfo,
  type LegalDocument,
} from '@escala/domain/legal';

/** "Versão 1.0 · atualizada em 15/10/2026". */
export function versionLine(doc: Pick<LegalDocument, 'version' | 'updatedAt'>): string {
  return `Versão ${doc.version} · atualizada em ${formatNumericDate(doc.updatedAt)}`;
}

/**
 * Uma categoria de cookies: título, se está ligada, a descrição e a lista do que guarda.
 * `control` é o interruptor (Preferências de cookies) ou o cadeado dos essenciais.
 */
export function CookieCategoryCard({
  category,
  state,
  control,
}: {
  category: CookieCategoryInfo;
  /** "Sempre ativos", "Ligados", "Desligados". */
  state: string;
  control?: ReactNode;
}) {
  return (
    <section
      aria-label={`Cookies ${category.title.toLowerCase()}`}
      className="overflow-hidden rounded-block border border-line-card"
    >
      <div className="flex items-start gap-3 px-3.5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="m-0 text-control font-bold">{category.title}</h3>
            <Badge size="sm" tone={category.required ? 'primary' : 'neutral'}>
              {state}
            </Badge>
          </div>
          <p className="m-0 mt-0.5 text-meta text-pretty text-ink-subtle">{category.description}</p>
        </div>
        {control ??
          (category.required ? (
            <Lock
              aria-label="Sempre ativos"
              className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-ink-subtle"
            />
          ) : null)}
      </div>
      {category.entries.length ? (
        <ul aria-label={`O que os ${category.title.toLowerCase()} guardam`} className="m-0 list-none p-0">
          {category.entries.map((entry) => (
            <li
              key={entry.name}
              className="grid gap-x-3 gap-y-0.5 border-t border-line-divider bg-surface-subtle px-3.5 py-2 text-meta md:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_minmax(0,9rem)]"
            >
              <span className="font-mono text-caption break-all text-ink-secondary">
                {entry.name}
                <span className="block font-sans text-ink-subtle">{COOKIE_KIND_LABEL[entry.kind]}</span>
              </span>
              <span className="text-ink-secondary">{entry.purpose}</span>
              <span className="text-ink-subtle">{entry.duration}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * O texto de um documento legal (seções com título, parágrafos e listas). Na Política de
 * Cookies, as categorias com tudo o que fica no navegador vêm logo depois.
 */
export function LegalContent({ doc, className }: { doc: LegalDocument; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-4.5', className)}>
      {doc.sections.map((section) => (
        <section key={section.heading} aria-label={section.heading} className="flex flex-col gap-1.5">
          <h3 className="m-0 text-body font-bold">{section.heading}</h3>
          {section.paragraphs.map((p) => (
            <p key={p} className="m-0 text-small leading-relaxed text-pretty text-ink-secondary">
              {p}
            </p>
          ))}
          {section.items?.length ? (
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-small leading-relaxed text-ink-secondary">
              {section.items.map((item) => (
                <li key={item} className="text-pretty">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
      {doc.key === 'cookies' ? (
        <div className="flex flex-col gap-2.5">
          {COOKIE_CATEGORIES.map((category) => (
            <CookieCategoryCard
              key={category.key}
              category={category}
              state={category.required ? 'Sempre ativos' : 'Opcional'}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
